/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import { element, string } from 'prop-types';
import get from 'lodash/get';
import SvgPortal from '../../../SvgPortal/SvgPortal';
import { shallowCompareObjects } from '../../../comparators/objectComparators';
import withPerformanceMeasurement from '../../../hoc/PerformanceObserver/HOCPerfMeasure';

/**
 * Renders an svg icon according to latest
 * UX guidelines/recommendations.
 */
const Icon = ({ automationId, children, height, width, ...additionalProps }) => {
    const config = {
        svg: {
            ...additionalProps,
            'data-automation-id': automationId,
            height,
            /**
             * In Edge 16 browser SVG icon click cancels the bubbling phase
             * of events so disabling pointer-events. As we dont have any
             * events on SVG adding it without browser specific hack.
             */
            style: {
                pointerEvents: 'none',
                ...additionalProps.style,
            },
            width,
        },
    };

    const name = get(children, 'type.displayName', '');

    if (children.props.id) {
        throw new Error('It is not supposed to pass id property to the icon, please remove it');
    }

    return name ? (
        <>
            <SvgPortal>
                {React.isValidElement(children) && React.cloneElement(children, { id: name })}
            </SvgPortal>
            <svg {...config.svg}>
                {/**
                 * xlinkHref is deprecated per MDN, however Safari 11 on OSX 10
                 * does not support the use of href so until we stop support of
                 * that browser we need to use xlinkHref instead.
                 * https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/xlink:href
                 */}
                <use xlinkHref={`#${name}`} />
            </svg>
        </>
    ) : null;
};

Icon.defaultProps = {
    automationId: '',
    height: '16',
    width: '16',
};

Icon.propTypes = {
    /** Icon requires an svg image as child. */
    children: element.isRequired,
    /** Unique ID for automation testing. */
    automationId: string,
    /** Height attribute for SVG element. */
    height: string,
    /** Width attribute for SVG element. */
    width: string,
};

export default React.memo(withPerformanceMeasurement(Icon), (prev, next) => {
    const { children: nChildren, ...nProps } = next;
    const { children: pChildren, ...pProps } = prev;
    const nChild = React.Children.only(nChildren);
    const pChild = React.Children.only(pChildren);

    return (
        shallowCompareObjects(nProps, pProps) &&
        nChild &&
        pChild &&
        nChild.type.displayName === pChild.type.displayName
    );
});
